import stc from 'string-to-color'
import Color from 'color'

const OTHERS_COLOR = '#c0c4cc'
const OTHERS_THRESHOLD = 0.01
const MAX_TOOLTIP_CAMPAIGNS = 10

const referrerColor = name => {
    let color = Color(stc(name))
    // Boost saturation for vibrancy without darkening
    if (color.saturationl() < 60) color = color.saturationl(60)
    return color.hex()
}

export { referrerColor, OTHERS_COLOR, OTHERS_THRESHOLD }

/**
 * Shared mixin for ReferrerChart components (Dashboard widget + Marketing partial).
 *
 * Consumers must provide a computed `rawReferrerData` that returns the array of
 * { referer, visitors, campaigns } objects to chart.
 */
export default {
    data() {
        return {
            chartData: null,
            legendItems: [],
            processedData: [],
            disabledSegments: [],
            // Chart.js plugin that renders visitor counts and percentages
            // directly onto each doughnut segment after the chart is drawn.
            doughnutLabelsPlugin: {
                id: 'doughnutLabels',
                afterDatasetsDraw: (chart) => {
                    const ctx = chart.ctx
                    const meta = chart.getDatasetMeta(0)
                    if (!meta || !meta.data) return

                    // Calculate the total across all segments to derive percentages
                    const total = meta.data.reduce((sum, arc, index) => {
                        const value = chart.data.datasets[0].data[index]
                        return sum + (value || 0)
                    }, 0)
                    if (!total) return

                    let stopped = false
                    meta.data.forEach((arc, i) => {
                        const value = chart.data.datasets[0].data[i]
                        if (!value) return
                        if (stopped) return

                        const percentage = Math.round(value / total * 100)
                        const startAngle = arc.startAngle
                        const endAngle = arc.endAngle
                        const angleSpan = endAngle - startAngle

                        if (angleSpan < 0.25) {
                            stopped = true
                            return
                        }

                        const midAngle = (startAngle + endAngle) / 2
                        const outerRadius = arc.outerRadius
                        const innerRadius = arc.innerRadius
                        const midRadius = (outerRadius + innerRadius) / 2
                        const arcWidth = outerRadius - innerRadius

                        if (arcWidth < 30) {
                            stopped = true
                            return
                        }

                        const arcLength = angleSpan * midRadius
                        if (arcLength < 30) {
                            stopped = true
                            return
                        }

                        // Position the label at the center of the arc segment
                        const x = arc.x + Math.cos(midAngle) * midRadius
                        const y = arc.y + Math.sin(midAngle) * midRadius

                        const segmentColor = Color(chart.data.datasets[0].backgroundColor[i] || '#000')
                        const textColor = segmentColor.contrast(Color('#fff')) >= 3 ? '#fff' : '#000'
                        // const strokeColor = segmentColor.darken(0.5).hex()

                        ctx.save()
                        ctx.textAlign = 'center'
                        ctx.textBaseline = 'middle'
                        // ctx.shadowColor = 'rgba(0,0,0,0.4)'
                        // ctx.shadowBlur = 4
                        // ctx.shadowOffsetX = 0
                        // ctx.shadowOffsetY = 1
                        // ctx.lineWidth = 1
                        // ctx.strokeStyle = strokeColor
                        ctx.fillStyle = textColor

                        if (arcLength > 50 && arcWidth > 40) {
                            ctx.font = 'bold 12px sans-serif'
                            // ctx.strokeText(value.toLocaleString(), x, y - 5)
                            ctx.fillText(value.toLocaleString(), x, y - 5)
                            ctx.font = '10px sans-serif'
                            // ctx.strokeText(percentage + '%', x, y + 8)
                            ctx.fillText(percentage + '%', x, y + 8)
                        } else {
                            ctx.font = 'bold 11px sans-serif'
                            // ctx.strokeText(percentage + '%', x, y)
                            ctx.fillText(percentage + '%', x, y)
                        }

                        ctx.restore()
                    })
                },
            },
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: '40%',
                animation: {
                    duration: 1000,
                },
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        callbacks: {
                            title: (tooltipItems) => {
                                if (!tooltipItems || !tooltipItems.length) return ''
                                return tooltipItems[0].label
                            },
                            label: (context) => {
                                const value = context.parsed || 0
                                const total = context.dataset.data.reduce((s, v) => s + v, 0)
                                const percentage = total > 0 ? Math.round(value / total * 100) : 0
                                return ' ' + value + ' ' + this.$t('visitors') + ' (' + percentage + '%)'
                            },
                            afterBody: (tooltipItems) => {
                                if (!tooltipItems || !tooltipItems.length) return ''
                                const index = tooltipItems[0].dataIndex
                                if (!this.processedData || !this.processedData[index]) return ''

                                const entry = this.processedData[index]

                                if (entry.isOthers && entry.othersDetail && entry.othersDetail.length) {
                                    const lines = ['']
                                    const detail = entry.othersDetail.slice(0, MAX_TOOLTIP_CAMPAIGNS)
                                    detail.forEach(item => {
                                        lines.push('  ' + item.referer + ': ' + item.visitors)
                                    })
                                    if (entry.othersDetail.length > MAX_TOOLTIP_CAMPAIGNS) {
                                        lines.push('  +' + (entry.othersDetail.length - MAX_TOOLTIP_CAMPAIGNS))
                                    }
                                    return lines
                                }

                                const campaigns = entry.campaigns || []
                                if (!campaigns.length) return ''

                                const lines = ['']
                                const shown = campaigns.slice(0, MAX_TOOLTIP_CAMPAIGNS)
                                shown.forEach(campaign => {
                                    lines.push('  ' + campaign.name + ': ' + campaign.visitors)
                                })
                                if (campaigns.length > MAX_TOOLTIP_CAMPAIGNS) {
                                    lines.push('  +' + (campaigns.length - MAX_TOOLTIP_CAMPAIGNS))
                                }
                                return lines
                            },
                        },
                    },
                },
            },
        }
    },
    methods: {
        toggleSegment(index, event) {
            if (event && (event.ctrlKey || event.metaKey)) {
                const allIndices = this.processedData.map((_, i) => i)
                const othersDisabled = allIndices.filter(i => i !== index)
                const isAlreadySolo = othersDisabled.every(i => this.disabledSegments.includes(i))
                this.disabledSegments = isAlreadySolo ? [] : othersDisabled
            } else {
                const position = this.disabledSegments.indexOf(index)
                if (position >= 0) {
                    this.disabledSegments.splice(position, 1)
                } else {
                    this.disabledSegments.push(index)
                }
            }
            this.generate()
        },
        generate() {
            const rawData = this.rawReferrerData

            if (!rawData.length) {
                this.chartData = null
                this.legendItems = []
                this.processedData = []
                return
            }

            const totalVisitors = rawData.reduce((sum, item) => sum + item.visitors, 0)

            const significant = []
            let othersVisitors = 0
            const othersDetail = []

            rawData.forEach(item => {
                if (totalVisitors > 0 && item.visitors / totalVisitors < OTHERS_THRESHOLD) {
                    othersVisitors += item.visitors
                    othersDetail.push({ referer: item.referer, visitors: item.visitors })
                } else {
                    significant.push(item)
                }
            })

            const processed = significant.slice()
            if (othersVisitors > 0) {
                processed.push({
                    referer: this.$t('Others'),
                    visitors: othersVisitors,
                    campaigns: [],
                    isOthers: true,
                    othersDetail: othersDetail,
                })
            }

            this.processedData = processed

            this.legendItems = processed.map(item => ({
                label: item.referer,
                color: item.isOthers ? OTHERS_COLOR : referrerColor(item.referer),
            }))

            const labels = []
            const values = []
            const colors = []

            processed.forEach((item, index) => {
                if (this.disabledSegments.includes(index)) return
                labels.push(item.referer)
                values.push(item.visitors)
                colors.push(item.isOthers ? OTHERS_COLOR : referrerColor(item.referer))
            })

            this.chartData = {
                labels: labels,
                datasets: [{
                    data: values,
                    backgroundColor: colors,
                    borderWidth: 2,
                    borderColor: '#fff',
                }],
            }

            this.$nextTick(() => {
                if (this.$refs.chart) {
                    this.$refs.chart.renderChart(this.chartData, this.chartOptions)
                }
            })
        },
    },
}
